const React = require('react');
const T = require('prop-types');
const { default: Styled } = require('styled-components');
const { default: TextField } = require('@mui/material/TextField');
const { transient$Props } = require('utils/styles');
const { singleLineFieldStyles } = require('hooks/useFormPrimitives/singleLineFieldStyles');

const { forwardRef } = React;

const internals = {};

const DEFAULT_ERR_COLOR = '#A7160C';
const DARK_BG_ERR_COLOR = '#F08983';
const LIGHT_BG_TEXT_COLOR = '#333333';

const LoginTextField = forwardRef((props, ref) => {

    const {
        error,
        hasLightBackground,
        onChange,
        inputProps,
        InputLabelProps,
        variant,
        ...rest
    } = props;

    const { StyledTextField } = internals;

    const errorMessage = error?.message || error;
    const hasError = !!errorMessage;
    const textFieldVariant = variant || (hasLightBackground ? 'filled' : 'outlined');

    const handleChange = (evt) => {

        if (onChange) {
            onChange(evt);
        }

        if (inputProps?.onChange) {
            inputProps.onChange(evt);
        }
    };

    return (
        <StyledTextField
            {...rest}
            variant={textFieldVariant}
            ref={ref}
            inputProps={inputProps}
            onChange={handleChange}
            fullWidth
            error={hasError}
            helperText={errorMessage}
            aria-invalid={hasError}
            $hasLightBackground={hasLightBackground}
            $textFieldVariant={textFieldVariant}
            InputLabelProps={{
                ...InputLabelProps,
                style: {
                    color: hasError
                        ? hasLightBackground ? DEFAULT_ERR_COLOR : DARK_BG_ERR_COLOR
                        : hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'
                }
            }}
        />
    );
});

LoginTextField.propTypes = {
    error: T.oneOfType([T.string, T.node, T.object]),
    hasLightBackground: T.bool,
    onChange: T.func,
    inputProps: T.shape({
        onChange: T.func
    }),
    InputLabelProps: T.object,
    variant: T.oneOf(['filled', 'outlined', 'standard'])
};

module.exports = LoginTextField;

internals.StyledTextField = Styled(TextField, transient$Props)`

    height: ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? '56px' : '72px'};
    font-size: ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? '1rem' : '21px'};
    margin-top: ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? '8px' : '12px'};
    color: ${({ $hasLightBackground }) => $hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'};

    input:-webkit-autofill {
        appearance: none;
        color: ${({ $hasLightBackground }) => $hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'} !important;
        -webkit-box-shadow: 0 0 0px 1000px ${({ theme, $hasLightBackground }) => $hasLightBackground ? 'white' : theme.palette.primary.main} inset;
        -webkit-text-fill-color: ${({ $hasLightBackground }) => $hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'} !important;
        border-radius: ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? '4px 4px 0 0' : '14px'};
        caret-color: ${({ $hasLightBackground }) => $hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'};
        font-size: ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? '1rem' : '21px'} !important;
        background-color: transparent !important;
    }

    input {
        height: ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? '1.4375em' : '30px'} !important;
    }

    & label {
        opacity: 1;
        font-size: ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? '1rem' : '21px'};

        &.Mui-focused {
            color: ${({ $hasLightBackground }) => $hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'};
            opacity: 1;
        }
    }

    & .MuiInputBase-root {
        padding-right: 0 !important;
    }

    & .MuiFilledInput-root {
        font-size: 1rem;
    }

    & .MuiOutlinedInput-root {
        border-radius: 14px;
        font-size: 1.05em;
        color: ${({ $hasLightBackground }) => $hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'};
        background-color: ${({ $hasLightBackground }) => $hasLightBackground ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.04)'};
        transition: border-color 120ms ease;

        & .MuiOutlinedInput-notchedOutline {
            border-width: 1px;
            border-color: ${({ $hasLightBackground }) => $hasLightBackground ? 'rgba(0, 0, 0, 0.35)' : 'rgba(255, 255, 255, 0.9)'};
        }

        &:hover .MuiOutlinedInput-notchedOutline,
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border-color: ${({ $hasLightBackground }) => $hasLightBackground ? LIGHT_BG_TEXT_COLOR : 'white'};
        }

        &.Mui-focused {
        }

        :-webkit-text-fill-color: unset;
    }

    & .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
        border-color: ${({ $hasLightBackground }) => $hasLightBackground ? DEFAULT_ERR_COLOR : DARK_BG_ERR_COLOR};
    }

    & .MuiFormHelperText-root.Mui-error {
        color: ${({ $hasLightBackground }) => $hasLightBackground ? DEFAULT_ERR_COLOR : DARK_BG_ERR_COLOR};
        font-size: 0.86rem;
        font-weight: 700;
    }

    & label.Mui-error {
        color: ${({ $hasLightBackground }) => $hasLightBackground ? DEFAULT_ERR_COLOR : DARK_BG_ERR_COLOR};
    }

    ${({ $textFieldVariant }) => $textFieldVariant === 'filled' ? singleLineFieldStyles : ''}
`;
